/**
 * The last thing standing between a render that threw and a blank white page.
 *
 * React unmounts the whole tree when a render throws, so without this the app
 * disappears and the only trace is a stack in a console nobody has open. Here the
 * failure is caught, shown as something readable, and posted to the error log — the
 * one class of failure the Worker cannot see for itself.
 */
import { Component } from 'react'
import { reportClientError } from '../lib/api'
import { Icon } from './Icon'

export default class ErrorBoundary extends Component {
  state = { error: null }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    reportClientError('render', error, {
      context: {
        // Which component broke, which is the only part of a React error that is
        // not in the stack, and the part that identifies it.
        component_stack: String(info?.componentStack ?? '').trim().slice(0, 1200),
      },
    })
  }

  render() {
    if (!this.state.error) return this.props.children
    return (
      <div className="page" style={{ maxWidth: 620 }}>
        <div className="col">
          <div className="notice notice-danger">
            <div className="notice-icon" aria-hidden="true">
              <Icon name="error" size={18} />
            </div>
            <div className="notice-body">
              <div className="notice-title">This page could not be drawn</div>
              <p className="small">
                Something went wrong rendering it. The failure has been written to the error log,
                so it can be looked at without you having to describe it.
              </p>
              <p className="mono tiny subtle">{this.state.error.message}</p>
              <div className="row" style={{ marginTop: 10 }}>
                <button className="btn btn-primary" onClick={() => window.location.reload()}>
                  Reload the page
                </button>
                {/* A document load rather than a route change, on purpose: the tree that
                    threw is still mounted, and the point of this button is to be rid of
                    it. `/dashboard` rather than `/`, which only redirects here anyway —
                    one request instead of two on the way out of a broken page. */}
                <a className="btn" href="/dashboard">
                  Go to the dashboard
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}
